'use client';

import { FC, useState, useMemo, useCallback } from 'react';
import { Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { StandardDialog } from '@/components/common/StandardDialog';
import { FormTextField, FormAutocomplete, FormDatePicker } from '@/components/common/FormComponents';
import { OptionKey, KeyValueType } from '@/lib/options/optionTypes';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useDialogStore, type BulkInstrumentRunDialogResult } from '@/stores/dialogStore';
import { useUserInteraction } from '@/hooks/useUnsavedChanges';
import { getBulkSubTechniques, findTechniqueById, buildTechniqueOptions } from '@/lib/utils/optionUtils';
import { getProblemDetail } from '@/lib/utils/apiErrors';

const OPTION_KEYS: OptionKey[] = [
  'synthesisTechniques',
  'synthesisInstruments',
  'synthesisInstrumentOptions',
  'researcherOptions',
];

/**
 * Dialog for creating a new instrument run (e.g., compounding step)
 * Based on NewBulkInstrumentRun from Knockout implementation
 */
export const NewBulkInstrumentRunDialog: FC = () => {
  const { bulkInstrumentRunDialog, closeBulkInstrumentRunDialog } = useDialogStore();
  const { options } = useSelectOptions(OPTION_KEYS);
  const { showSnackbar } = useSnackbar();

  const { title, initialTechnique, initialInstrument, sampleLabel: initialSampleLabel } = bulkInstrumentRunDialog;
  const isOpen = bulkInstrumentRunDialog.isOpen;

  // Initialize form state based on dialog config
  const initializeTechnique = useMemo(() => {
    if (initialTechnique && options.synthesisTechniques) {
      const tech = options.synthesisTechniques.find((t) => t.id === initialTechnique);
      return tech ? { id: tech.id!, name: tech.name || tech.id! } : null;
    }
    return null;
  }, [initialTechnique, options.synthesisTechniques]);

  const initializeInstrument = useMemo(() => {
    if (initialInstrument && initializeTechnique) {
      // Find instrument from options that match the initial technique
      const baseInstruments = options.synthesisInstruments || [];
      const formatted = options.synthesisInstrumentOptions || [];

      const filteredIds = new Set(
        baseInstruments
          .filter((inst) => inst.synthesisTechniques?.includes(initializeTechnique.id))
          .map((inst) => inst.id)
      );

      const instrument = formatted.find((opt) => opt.key === initialInstrument && filteredIds.has(opt.key));
      return instrument ? { id: instrument.key, name: instrument.value || instrument.key } : null;
    }
    return null;
  }, [initialInstrument, initializeTechnique, options.synthesisInstruments, options.synthesisInstrumentOptions]);

  // Form state
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [selectedStaff, setSelectedStaff] = useState<KeyValueType | null>(() => {
    // Initialize with first researcher if available
    return options.researcherOptions?.[0] || null;
  });
  const [selectedTechnique, setSelectedTechnique] = useState<{ id: string; name: string } | null>(initializeTechnique);
  const [selectedInstrument, setSelectedInstrument] = useState<{ id: string; name: string } | null>(
    initializeInstrument
  );

  // Error states
  const [staffError, setStaffError] = useState(false);
  const [techniqueError, setTechniqueError] = useState(false);
  const [instrumentError, setInstrumentError] = useState(false);

  const { hasUnsavedChanges, markAsInteracted } = useUserInteraction();

  // Filter instruments based on selected technique
  const instrumentOptions = useMemo(() => {
    if (!selectedTechnique?.id) return [];

    const baseInstruments = options.synthesisInstruments || [];
    const formatted = options.synthesisInstrumentOptions || [];

    const filteredIds = new Set(
      baseInstruments.filter((inst) => inst.synthesisTechniques?.includes(selectedTechnique.id)).map((inst) => inst.id)
    );

    return formatted
      .filter((opt) => filteredIds.has(opt.key))
      .map((opt) => ({
        id: opt.key,
        name: opt.value || opt.key,
      }));
  }, [selectedTechnique, options.synthesisInstruments, options.synthesisInstrumentOptions]);

  // Reset form and close
  const handleClose = useCallback(() => {
    setSelectedDate(new Date());
    setSelectedStaff(options.researcherOptions?.[0] || null);
    setSelectedTechnique(initializeTechnique);
    setSelectedInstrument(initializeInstrument);
    setStaffError(false);
    setTechniqueError(false);
    setInstrumentError(false);
    closeBulkInstrumentRunDialog(null);
  }, [closeBulkInstrumentRunDialog, options.researcherOptions, initializeTechnique, initializeInstrument]);

  const handleSubmit = useCallback(async () => {
    let hasError = false;

    if (!selectedStaff) {
      setStaffError(true);
      hasError = true;
    } else {
      setStaffError(false);
    }

    if (!selectedTechnique) {
      setTechniqueError(true);
      hasError = true;
    } else {
      setTechniqueError(false);
    }

    if (!selectedInstrument) {
      setInstrumentError(true);
      hasError = true;
    } else {
      setInstrumentError(false);
    }

    if (hasError) {
      showSnackbar('Please fill out all required fields.', 'error');
      return;
    }

    if (!selectedDate) {
      showSnackbar('Please select a date.', 'error');
      return;
    }

    if (!selectedInstrument || !selectedTechnique) {
      showSnackbar('Please fill out all required fields.', 'error');
      return;
    }

    try {
      const result: BulkInstrumentRunDialogResult = {
        date: selectedDate,
        instrument: selectedInstrument.id,
        technique: selectedTechnique.id,
      };

      closeBulkInstrumentRunDialog(result);
    } catch (error: unknown) {
      showSnackbar(getProblemDetail(error) ?? 'Failed to prepare bulk instrument run.', 'error');
    }
  }, [selectedStaff, selectedTechnique, selectedInstrument, selectedDate, showSnackbar, closeBulkInstrumentRunDialog]);

  // Technique options - default to Compounding if initialTechnique is set
  const techniqueOptions = useMemo(() => {
    if (initialTechnique) {
      const tech = findTechniqueById(options.synthesisTechniques, initialTechnique);
      return tech ? [{ id: tech.id!, name: tech.name || tech.id! }] : [];
    }
    return buildTechniqueOptions(getBulkSubTechniques(options.synthesisTechniques));
  }, [initialTechnique, options.synthesisTechniques]);

  return (
    <StandardDialog
      open={isOpen}
      onClose={handleClose}
      title={title || 'Add Instrument Run'}
      maxWidth="md"
      onSubmit={handleSubmit}
      submitText="Add Instrument Run"
      hasUnsavedChanges={hasUnsavedChanges}
      unsavedChangesMessage="You have unsaved changes. Are you sure you want to discard them?"
    >
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        {/* Sample ID (read-only) */}
        {initialSampleLabel ? (
          <Box mb={2}>
            <FormTextField label="Sample ID" value={initialSampleLabel} fullWidth disabled />
          </Box>
        ) : null}

        {/* Created by and Date */}
        <Box display="flex" gap={2} mb={2}>
          <Box flex={1}>
            <FormAutocomplete
              options={options.researcherOptions ?? []}
              getOptionLabel={(option) => option?.value || ''}
              value={selectedStaff}
              onChange={(_, newValue) => {
                setSelectedStaff(newValue);
                markAsInteracted();
              }}
              label="Created by"
              required
              error={staffError}
            />
          </Box>
          <Box flex={1}>
            <FormDatePicker
              label="Date"
              value={selectedDate}
              onChange={(newValue) => {
                setSelectedDate(newValue);
                markAsInteracted();
              }}
              required
            />
          </Box>
        </Box>

        {/* Method and Instrument */}
        <Box display="flex" gap={2} mb={2}>
          <Box flex={1}>
            <FormAutocomplete
              options={techniqueOptions}
              getOptionLabel={(option) => option?.name || option?.id || ''}
              value={selectedTechnique}
              onChange={(_, newValue) => {
                setSelectedTechnique(newValue);
                // Reset instrument when technique changes
                setSelectedInstrument(null);
                markAsInteracted();
              }}
              label="Method"
              required
              error={techniqueError}
              disabled={!!initialTechnique} // Disable if technique is pre-set
            />
          </Box>
          <Box flex={1}>
            <FormAutocomplete
              options={instrumentOptions}
              getOptionLabel={(option) => option?.name || option?.id || ''}
              value={selectedInstrument}
              onChange={(_, newValue) => {
                setSelectedInstrument(newValue);
                markAsInteracted();
              }}
              label="Target Instrument"
              required
              error={instrumentError}
            />
          </Box>
        </Box>
      </LocalizationProvider>
    </StandardDialog>
  );
};
